'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  LayoutDashboard,
  Users,
  FileText,
  ShieldCheck,
  Bell,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  ClipboardList,
  BarChart3,
  BookOpen,
  AlertTriangle,
  Download,
  Home,
  HardDrive,
  GraduationCap,
  LifeBuoy,
  FlaskConical,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  badgeKey?: string;
  group: string;
}

const navItems: NavItem[] = [
  { id: 'nav-dashboard', label: 'Dashboard', href: '/admin-dashboard', icon: LayoutDashboard, group: 'main' },
  { id: 'nav-students', label: 'Student Records', href: '/admin-dashboard?tab=students', icon: Users, badgeKey: 'students', group: 'main' },
  { id: 'nav-submissions', label: 'Submissions', href: '/admin-dashboard?tab=submissions', icon: FileText, badgeKey: 'submissions', group: 'main' },
  { id: 'nav-verification', label: 'Document Review', href: '/admin-dashboard?tab=review', icon: ShieldCheck, badgeKey: 'review', group: 'main' },
  { id: 'nav-documents', label: 'Student Documents', href: '/documents', icon: FolderOpen, group: 'main' },
  { id: 'nav-programmes', label: 'Programmes', href: '/admin-dashboard?tab=programmes', icon: BookOpen, group: 'academic' },
  { id: 'nav-cohorts', label: 'Cohorts', href: '/admin-dashboard?tab=cohorts', icon: ClipboardList, group: 'academic' },
  { id: 'nav-reports', label: 'Reports', href: '/admin-dashboard?tab=reports', icon: BarChart3, group: 'academic' },
  { id: 'nav-download-forms', label: 'Download Forms', href: '/download-form', icon: Download, group: 'academic' },
  { id: 'nav-student-dashboard', label: 'Student Dashboard', href: '/student-dashboard', icon: GraduationCap, group: 'academic' },
  { id: 'nav-alerts', label: 'Alerts', href: '/admin-dashboard?tab=alerts', icon: AlertTriangle, badgeKey: 'alerts', group: 'system' },
  { id: 'nav-notifications', label: 'Notifications', href: '/admin-dashboard?tab=notifications', icon: Bell, group: 'system' },
  { id: 'nav-support-dashboard', label: 'Support Tickets', href: '/support-dashboard', icon: LifeBuoy, group: 'system' },
  { id: 'nav-drive', label: 'Google Drive', href: '/admin-settings?section=drive', icon: HardDrive, group: 'system' },
  { id: 'nav-settings', label: 'Settings', href: '/admin-settings', icon: Settings, group: 'system' },
  { id: 'nav-testing', label: 'System Testing', href: '/admin-testing-panel', icon: FlaskConical, group: 'system' },
];

const groupLabels: Record<string, string> = {
  main: 'Operations',
  academic: 'Academic',
  system: 'System',
};

interface SidebarProps {
  activePath?: string;
}

export default function Sidebar({ activePath = '/admin-dashboard' }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [badges, setBadges] = useState<Record<string, number>>({});
  const [adminName, setAdminName] = useState('Ziva Admin');
  const [adminEmail, setAdminEmail] = useState('admin@myzivasol.com');
  const router = useRouter();

  useEffect(() => {
    async function fetchBadges() {
      try {
        const supabase = createClient();

        const [studentsRes, submissionsRes, reviewRes, alertsRes] = await Promise.all([
          supabase.from('user_profiles').select('id', { count: 'exact', head: true }).eq('role', 'student'),
          supabase.from('student_submissions').select('id', { count: 'exact', head: true }).in('overall_status', ['pending', 'under_review']),
          supabase.from('document_records').select('id', { count: 'exact', head: true }).eq('status', 'pending_review'),
          supabase.from('document_records').select('id', { count: 'exact', head: true }).eq('status', 'rejected'),
        ]);

        setBadges({
          students: studentsRes.count || 0,
          submissions: submissionsRes.count || 0,
          review: reviewRes.count || 0,
          alerts: alertsRes.count || 0,
        });
      } catch {
        // silently fail
      }
    }

    async function fetchAdminProfile() {
      try {
        const supabase = createClient();
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('first_name, last_name')
            .eq('id', user.id)
            .single();
          if (profile) {
            const name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
            if (name) setAdminName(name);
          }
          if (user.email) setAdminEmail(user.email);
        }
      } catch {
        // silently fail
      }
    }

    fetchBadges();
    fetchAdminProfile();
  }, []);

  const handleSignOut = async () => {
    const supabase = createClient();
    await supabase.auth.signOut();
    router.push('/');
  };

  const groups = ['main', 'academic', 'system'];

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className={`flex items-center ${collapsed ? 'justify-center px-3' : 'justify-between px-4'} py-4 border-b border-border`}>
        {!collapsed && (
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg overflow-hidden bg-primary/10 flex-shrink-0">
              <Image
                src="/assets/images/logo-1778027352072.jpeg"
                alt="Ziva Niara Solutions logo"
                width={32}
                height={32}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="min-w-0">
              <span className="block text-sm font-bold text-primary leading-tight truncate">Ziva DocuSecure</span>
              <span className="block text-[10px] text-muted-foreground leading-tight truncate">Niara Solutions</span>
            </div>
          </div>
        )}
        {collapsed && (
          <div className="w-8 h-8 rounded-lg overflow-hidden bg-primary/10">
            <Image src="/assets/images/logo-1778027352072.jpeg" alt="Ziva Niara Solutions logo" width={32} height={32} className="w-full h-full object-cover" />
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex items-center justify-center w-6 h-6 rounded-md hover:bg-muted text-muted-foreground transition-colors"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      {/* Role badge */}
      {!collapsed && (
        <div className="mx-4 mt-3 px-3 py-1.5 bg-primary/10 rounded-lg">
          <span className="text-xs font-semibold text-primary">Admin Portal</span>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2">
        {/* Home link */}
        <div className="mb-2">
          <Link
            href="/"
            className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
            title={collapsed ? 'Home' : undefined}
          >
            <Home size={16} className="flex-shrink-0" />
            {!collapsed && <span>Public Home</span>}
          </Link>
        </div>

        {groups.map((group) => {
          const items = navItems.filter((i) => i.group === group);
          return (
            <div key={`group-${group}`} className="mb-4">
              {!collapsed && (
                <p className="px-2 mb-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                  {groupLabels[group]}
                </p>
              )}
              {items.map((item) => {
                const Icon = item.icon;
                const badgeCount = item.badgeKey ? (badges[item.badgeKey] || 0) : 0;
                const isActive = activePath === item.href;
                return (
                  <Link
                    key={item.id}
                    href={item.href}
                    className={`
                      flex items-center ${collapsed ? 'justify-center' : 'justify-between'} gap-2.5
                      px-2.5 py-2 rounded-lg mb-0.5 text-sm font-medium transition-all duration-150
                      group relative
                      ${isActive
                        ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }
                    `}
                    title={collapsed ? item.label : undefined}
                  >
                    <div className="flex items-center gap-2.5">
                      <Icon size={16} className="flex-shrink-0" />
                      {!collapsed && <span>{item.label}</span>}
                    </div>
                    {!collapsed && item.badgeKey !== undefined && badgeCount > 0 && (
                      <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
                        {badgeCount}
                      </span>
                    )}
                    {collapsed && item.badgeKey !== undefined && badgeCount > 0 && (
                      <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-primary" />
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* User footer */}
      <div className={`border-t border-border p-3 ${collapsed ? 'flex justify-center' : ''}`}>
        {!collapsed ? (
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-white">
                {adminName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-foreground truncate">{adminName}</p>
              <p className="text-[10px] text-muted-foreground truncate">{adminEmail}</p>
            </div>
            <button onClick={handleSignOut} className="text-muted-foreground hover:text-red-500 transition-colors" title="Sign out">
              <LogOut size={14} />
            </button>
          </div>
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
            <span className="text-xs font-bold text-white">
              {adminName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`
          hidden lg:flex flex-col h-screen sticky top-0 bg-card border-r border-border
          transition-all duration-300 ease-in-out flex-shrink-0
          ${collapsed ? 'w-16' : 'w-60'}
        `}
      >
        {sidebarContent}
      </aside>

      {/* Mobile toggle */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-40 w-9 h-9 flex items-center justify-center rounded-lg bg-card border border-border shadow-card text-foreground"
        aria-label="Open menu"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 h-full bg-card border-r border-border flex flex-col animate-slide-up">
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
}